import { useTranslation } from "react-i18next"
import { ProductFruits } from "react-product-fruits"
import { Outlet } from "react-router"
import { faro } from "@grafana/faro-react"
import { AzureAdLoginRedirect, getUser, performLogout, useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { usePatientFileLockRequestNotificationHandler } from "@/hooks/usePatientFileLockRequestNotificationHandler"
import { useRegisterNotificationServiceWorker } from "@/hooks/useRegisterNotificationServiceWorker"
import { useSharedMedicalRecordEventsCrossWindowSynchronization } from "@/hooks/useSharedMedicalRecordEventsCrossWindowSynchronization"
import { useWebNotificationsSubscription } from "@/hooks/useWebNotificationsSubscription"
import { AppearanceSynchronizer } from "@/layouts/appearance-section"
import { autoForwardingMockTime } from "@/lib/auto-forwarding-mock-time"
import { fetchServiceMapping } from "@/lib/fetch-service-mappings"
import { fetchUsers } from "@/lib/fetch-users"
import { fetchTimeZone } from "@/lib/initial-time-zone"
import { useMedicalRecordSharedWithMeChangedSubscription } from "@/modules/patients/services/useMedicalRecordSharedWithMeChangedSubscription"

const reportStartupTaskFailure = (startupTaskName: string) => (error: unknown) => {
  // Expected, and already recorded as an event by the redirect itself.
  if (error instanceof AzureAdLoginRedirect) {
    return
  }

  const startupError = error instanceof Error ? error : new Error(String(error))

  // The unpatched console makes the error show up in the console, but not reported to Grafana.
  const unpatchedConsole = faro?.unpatchedConsole ?? console
  unpatchedConsole.error(`${startupTaskName} failed`, startupError)

  faro?.api?.pushError(startupError, {
    context: { startupTask: startupTaskName },
  })
}

export async function rootLoader() {
  try {
    const { user } = await getUser()
    const storedUserId = localStorage.getItem("currentUserId")

    if (storedUserId && storedUserId !== user.objectId) {
      localStorage.clear()
      sessionStorage.clear()
      localStorage.setItem("currentUserId", user.objectId)
      window.location.reload()
      return user
    }

    if (!storedUserId) {
      localStorage.setItem("currentUserId", user.objectId)
    }

    // As they are not awaited here, we handle failures individually instead of relying on the try/catch (which would not work)
    fetchTimeZone.catch(reportStartupTaskFailure("fetchTimeZone"))
    fetchServiceMapping().catch(reportStartupTaskFailure("fetchServiceMapping"))
    fetchUsers().catch(reportStartupTaskFailure("fetchUsers"))

    return user
  } catch (error) {
    // The BFF session is invalid and we redirect the user to Entra ID. This is expected behaviour.
    if (error instanceof AzureAdLoginRedirect) {
      return null
    }

    reportStartupTaskFailure("getUser")(error)

    return null
  }
}

export const App = () => {
  const { t } = useTranslation()
  const user = useUser()
  const currentLanguage = useCurrentLanguage()

  // auto-forwarding mock time in dev mode
  autoForwardingMockTime()

  useRegisterNotificationServiceWorker()
  useWebNotificationsSubscription()
  usePatientFileLockRequestNotificationHandler()
  useMedicalRecordSharedWithMeChangedSubscription()
  useSharedMedicalRecordEventsCrossWindowSynchronization()

  if (!user) {
    return
  }

  const logout = () => {
    performLogout(user)
  }

  if (!user.role || !user.countries?.length) {
    return (
      <>
        <div>{`${t("error.unauthorized.title")}: ${t("error.unauthorized.missingRoleOrCountry")}`}</div>
        <Button
          data-testid="global-logout-button"
          variant="unstyle"
          className="text-critical hover:bg-critical-surface"
          onClick={logout}>
          <span>{t("common.button.logout")}</span>
        </Button>
      </>
    )
  }

  const userInfo = {
    username: user.objectId,
    role: user.role,
  }

  return (
    <>
      <ProductFruits workspaceCode="QofEwYlWbN1N5qH3" language={currentLanguage} user={userInfo} />
      <Outlet />
      <AppearanceSynchronizer />
    </>
  )
}
